import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from '@env/environment';
import { sanitizeTelemetry } from './faro-privacy';

/**
 * Build the cross-origin allow-list for W3C trace-context propagation.
 *
 * The browser only attaches `traceparent`/`tracestate` to a request when its
 * URL matches one of these patterns — scoped to the backend API origin so we
 * never add trace headers to third-party calls, which would trip their CORS
 * preflight. A relative apiUrl (local dev proxy) is same-origin and propagated
 * by default, so no pattern is needed.
 *
 * The backend CORS allow-list explicitly permits traceparent/tracestate/
 * baggage (backend AD-051), so these headers pass preflight there and admin
 * spans link to backend spans into one distributed trace.
 */
function backendTracePropagationTargets(apiUrl: string): (string | RegExp)[] {
  try {
    if (!/^https?:\/\//i.test(apiUrl)) {
      return [];
    }
    const origin = new URL(apiUrl).origin;
    const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Anchor to the FULL origin and require an origin boundary next — a port,
    // path, query, fragment, or end-of-URL. Without the boundary, `^origin`
    // also matches look-alike hosts where the real origin is a prefix
    // (e.g. https://…normaxcapital.com.evil.com), which would propagate
    // trace headers to an unintended third-party origin.
    return [new RegExp(`^${escaped}(?:[:/?#]|$)`)];
  } catch {
    return [];
  }
}

export function faroInitializer(): () => void {
  return () => {
    const { telemetry, apiUrl } = environment;

    // Disabled in local dev (and any build without a collector URL): the SDK
    // starts nothing, so no telemetry leaves the browser.
    if (!telemetry?.enabled || !telemetry.collectorUrl) {
      return;
    }

    initializeFaro({
      url: telemetry.collectorUrl,
      app: {
        name: 'Normax Trust Banking Admin',
        version: '0.0.0',
        environment: telemetry.environment,
      },
      // Strip id-like segments from every URL before it leaves the browser
      // (page meta, navigation events, HTTP trace spans). See faro-privacy.ts.
      beforeSend: sanitizeTelemetry,
      instrumentations: [
        // captureConsole: false — admin/KYC flows may log account or PII data;
        // errors still reach Faro via the custom ErrorHandler (pushError).
        ...getWebInstrumentations({ captureConsole: false }),
        new TracingInstrumentation({
          instrumentationOptions: {
            // Propagate trace context to the backend so admin↔backend spans
            // join into one trace (Tempo). Scoped to the backend origin only.
            propagateTraceHeaderCorsUrls: backendTracePropagationTargets(apiUrl),
          },
        }),
      ],
      // Angular Router has no dedicated Faro integration — capture URL changes
      // and navigation timing automatically instead.
      experimental: { trackNavigation: true },
      ignoreErrors: [
        // Layout quirks — harmless, not real errors
        /^ResizeObserver loop limit exceeded$/,
        /^ResizeObserver loop completed with undelivered notifications$/,
        // Cross-origin scripts with no useful stack
        /^Script error\.$/,
        // Browser extension interference
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
      ],
    });
  };
}
